// The status card at the top of the list: two pages that swipe, the points
// scoreboard (the next goal) and the activity grid (the habit), with dots
// under them. The page you left it on is remembered on this device, as the
// iOS card's is. The pages share a height, so the surface holds still as
// you swipe. The points include the setup list's welcome points, which live
// on this device and never sync.
import { useLayoutEffect, useRef, useState } from 'react';
import { isComplete, type Project } from '@shared/plan';
import { levelProgress, totalPoints } from '@shared/score';
import { ScoreCard, reducedMotion } from '@app/ui';
import { useWelcomePoints } from '../../onboarding/setupStore';
import { ActivityCard } from './ActivityGrid';

const PAGE_KEY = 'hd:statusCard';
const PAGES = ['Points', 'Activity'] as const;
const panelId = 'status-pages';
const readPage = (): number => { try { return localStorage.getItem(PAGE_KEY) === '1' ? 1 : 0; } catch { return 0; } };
const savePage = (i: number): void => { try { localStorage.setItem(PAGE_KEY, String(i)); } catch { /* a private window; the page is still shown */ } };

export function StatusPager({ projects }: { projects: readonly Project[] }) {
  const live = projects.filter((p) => p.deletedAt === undefined);
  const total = totalPoints(live) + useWelcomePoints();
  const { level, next, fraction, toNext } = levelProgress(total);
  const badges = live.filter(isComplete).length;
  const [page, setPage] = useState(readPage);
  const pages = useRef<HTMLDivElement>(null);

  const left = (i: number): number => (pages.current?.children[i] as HTMLElement | undefined)?.offsetLeft ?? 0;
  // Land on the remembered page before the first paint.
  useLayoutEffect(() => { if (pages.current && page === 1) pages.current.scrollLeft = left(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onScroll = () => {
    const el = pages.current;
    if (!el) return;
    const i = el.scrollLeft >= left(1) / 2 ? 1 : 0;
    if (i !== page) { setPage(i); savePage(i); }
  };
  const go = (i: number) => pages.current?.scrollTo({ left: left(i), behavior: reducedMotion() ? 'instant' : 'smooth' });

  // The dots are tabs and the scroller is their panel (iOS calls this a
  // TabView), which also makes the panel focusable so a keyboard can scroll it.
  return (
    <div className="status">
      <div className="status-pages" id={panelId} ref={pages} onScroll={onScroll} tabIndex={0} role="tabpanel" aria-label="Status">
        <div className="status-page"><ScoreCard points={total} level={level.name} next={next?.name} toNext={toNext} fraction={fraction} badges={badges} /></div>
        <div className="status-page"><ActivityCard projects={live} /></div>
      </div>
      <div className="pager" role="tablist" aria-label="Status pages">
        {PAGES.map((name, i) => (
          <button key={name} type="button" role="tab" className={page === i ? 'on' : undefined} aria-label={name} aria-selected={page === i} aria-controls={panelId} onClick={() => go(i)} />
        ))}
      </div>
    </div>
  );
}
